import { useEffect, useRef } from 'react';
import { BehaviorSubject, distinctUntilChanged } from 'rxjs';
import { useSnackbar } from '../Context/SnackbarContext';

type ErrorDomainKeys =
  | 'user'
  | 'general'
  | 'commission'
  | 'customer'
  | 'link-event'
  | 'ap-map'
  | 'link'
  | 'tsd-map'
  | 'supplier-map'
  | 'product-map'
  | 'asset'
  | 'comment'
  | 'attachment'
  | 'run-total'
  | 'provisional-asset'
  | 'rpm-account'
  | 'search'
  | 'ticket'
  | 'user-customization'
  | 'rpm-run'
  | 'etu'
  | 'tfu'
  | 'tfu-settings'
  | 'product'
  | 'run-total-audit'
  | 'customer-map'
  | 'run'
  | 'payment'
  | 'payment-recipient'
  | 'charts'
  | 'attribution'
  | 'spiff'
  | 'correction'
  | 'adjustment'
  | 'change-history'
  | 'mcp-access'
  | 'live-updates'
  | 'live-updates-run-totals'
  | 'live-updates-link-allocations';
type ErrorsByDomain = Record<ErrorDomainKeys, string | undefined>;

const defaultErrorByDomain: ErrorsByDomain = {
  user: undefined,
  general: undefined,
  commission: undefined,
  customer: undefined,
  'link-event': undefined,
  'ap-map': undefined,
  link: undefined,
  'tsd-map': undefined,
  'supplier-map': undefined,
  'product-map': undefined,
  search: undefined,
  ticket: undefined,
  asset: undefined,
  comment: undefined,
  attachment: undefined,
  'run-total': undefined,
  'provisional-asset': undefined,
  'rpm-account': undefined,
  'user-customization': undefined,
  'rpm-run': undefined,
  etu: undefined,
  tfu: undefined,
  'tfu-settings': undefined,
  product: undefined,
  'run-total-audit': undefined,
  'customer-map': undefined,
  run: undefined,
  payment: undefined,
  'payment-recipient': undefined,
  charts: undefined,
  attribution: undefined,
  spiff: undefined,
  correction: undefined,
  adjustment: undefined,
  'change-history': undefined,
  'mcp-access': undefined,
  'live-updates': undefined,
  'live-updates-run-totals': undefined,
  'live-updates-link-allocations': undefined
};

const defaultUserErrorMessage = undefined;
const errorByDomainSubject = new BehaviorSubject<ErrorsByDomain>(defaultErrorByDomain);
const userErrorMessageSubject = new BehaviorSubject<string | undefined>(defaultUserErrorMessage);
export const getUserErrorMessageSubject = (): BehaviorSubject<string | undefined> => userErrorMessageSubject;
export const getErrorByDomainSubject = (): BehaviorSubject<ErrorsByDomain> => errorByDomainSubject;

const updateErrorMessage = (message: string | undefined): void => userErrorMessageSubject.next(message);
export const getErrorMessageSubject = () => userErrorMessageSubject;
export const addError = (domain: ErrorDomainKeys, message: string | undefined): void => {
  errorByDomainSubject.next({ ...errorByDomainSubject.value, [domain]: message });
  handleErrorsByDomainUpdate();
};
export const clearError = (domain: ErrorDomainKeys): void => {
  errorByDomainSubject.next({ ...errorByDomainSubject.value, [domain]: undefined });
  handleErrorsByDomainUpdate();
};
export const resetErrorStore = (): void => {
  errorByDomainSubject.next(defaultErrorByDomain);
  userErrorMessageSubject.next(defaultUserErrorMessage);
};

// Re-emits even with no errors left, so clearError can retract a message it set.
const handleErrorsByDomainUpdate = (): void => {
  const errorsArray = Object.keys(errorByDomainSubject.getValue()).reduce((msgs: string[], key: string): string[] => {
    const currError = errorByDomainSubject.getValue()[key as unknown as ErrorDomainKeys];
    if (currError && !msgs.includes(currError)) msgs.push(currError);

    return msgs;
  }, []);

  updateErrorMessage(errorsArray.join(', \n') || undefined);
};

export const useErrorStore = (): void => {
  const { setSnack, snack } = useSnackbar();

  // Live snapshot of the shared snack, so a retraction can check what's actually on screen
  // right now instead of what was there when this effect was set up.
  const snackRef = useRef(snack);
  useEffect(() => {
    snackRef.current = snack;
  });

  useEffect(() => {
    // Dedup: a no-op clearError still re-emits, which would otherwise close an unrelated snack.
    const errorMsgSub = userErrorMessageSubject.pipe(distinctUntilChanged()).subscribe((errors) => {
      if (!setSnack) return;
      if (errors) {
        setSnack({ message: errors, type: 'error', open: true, duration: 1000000 });
      } else if (snackRef.current?.type === 'error') {
        // Only close the snack this store opened -- an unrelated one (e.g. a success toast) stays up.
        setSnack({ ...snackRef.current, message: '', open: false });
      }
    });

    return () => {
      if (errorMsgSub) errorMsgSub.unsubscribe();
    };
  }, [setSnack]);
};
